// conventions-allow: file-size - shared Pusher subscription orchestration intentionally remains in one module
import { refetchOperationalEventsAfterInFlight } from 'api/events/events.invalidation.helper'
import {
  MFRR_CM_BIDS_QUERY_KEY,
  MFRR_DELIVERED_BIDS_QUERY_KEY,
  MFRR_MARKET_RESULTS_QUERY_KEY,
} from 'api/mfrr/mfrr.constants'
import type { MfrrMarket } from 'api/mfrr/mfrr.types'
import { NOTIFICATIONS_QUERY_KEY } from 'api/notifications/notifications.api'
import {
  OPT_JOB_WITH_QUEUE_ITEMS_QUERY_KEY,
  OPT_JOBS_AND_QUEUE_ITEMS_STATUS_QUERY_KEY,
  OPT_JOBS_QUERY_KEY,
  type OptJobStatus,
  patchOptJobWithQueueItemsCache,
} from 'api/optJobs/optJobs.api'

import { OPT_PROJECTS_QUERY_KEY } from 'api/optProjects/optProjects.api'
import { QueueItemSchema } from 'api/queueItems/queueItems.types'
import { useSettlementExportUpdates } from 'api/settlementExports/settlementExports.api'
import { SYSTEM_QUERY_KEY, SYSTEMS_QUERY_KEY } from 'api/systems/systems.api'
import { UNITS_QUERY_KEY } from 'api/units/units.api'
import { useUser } from 'api/users/users.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import { queryClient } from 'helpers/queryClient'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import authStore from 'store/auth/auth'
import globalStore from 'store/global/global'
import mfrrStore, { type MfrrViewedBidWindow } from 'store/mfrr/mfrr'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { useChannel, useEvent } from 'ui/components/PusherContext/PusherContext'
import Datetime from 'utils/datetime/datetime'
import getLogger from 'utils/log/log'
import { useSystemFeatureFlagIsActive } from 'utils/systemUtils'
import { useSnapshot } from 'valtio'
import {
  alertProps,
  electricityPriceScenarioConfig,
  followUpConfig,
  fuelPlanConfig,
  invalidateOptProjectCommonQueries,
  invalidateOptProjectFinishedQueries,
  measvaluesCalculationsConfig,
  type OptimizationEventData,
  type OptimizationHandlerContext,
  type OptimizationTypeConfig,
  publishedRegularConfig,
  realTimeConfig,
  regularConfig,
  regularWipConfig,
  sandboxConfig,
  updateOptProjectStatus,
} from './PusherSubscriptions.helpers'

const log = getLogger('PusherSubscriptions')

/**
 * The job-level statuses that change which rows the opt job log has to show: a job
 * enters the log when it is created, and moves from Running to Completed when it
 * reaches any end state — finished, failed or unsolvable alike. Every transition in
 * between is queue-item progress, which arrives on `queueitem-updated` and is patched
 * straight into the cache — so refetching on those would throw away a cheap in-place
 * update and re-request the whole day's log for no visible change.
 */
const OPT_JOB_WITH_QUEUE_ITEM_LOG_REFETCH_STATUSES: ReadonlySet<OptJobStatus> = new Set([
  'Created',
  'Finished',
  'Failed',
  'Unsolvable',
])

type MfrrBidsEventData = {
  market: MfrrMarket
  start_time: ISODateTime
  end_time: ISODateTime
}

function isEventInViewedMfrrBidWindow(
  viewedBidWindow: MfrrViewedBidWindow | undefined,
  data: unknown
): data is MfrrBidsEventData {
  if (!viewedBidWindow || typeof data !== 'object' || data === null) return false

  const { market, start_time, end_time } = data as Partial<MfrrBidsEventData>
  if (market !== viewedBidWindow.market || !start_time || !end_time) return false

  const eventStart = new Date(start_time).getTime()
  const eventEnd = new Date(end_time).getTime()
  if (!Number.isFinite(eventStart) || !Number.isFinite(eventEnd) || eventEnd < eventStart) return false

  const viewedStart = new Date(viewedBidWindow.startTime).getTime()
  const viewedEnd = new Date(viewedBidWindow.endTime).getTime()

  return eventStart <= viewedEnd && eventEnd >= viewedStart
}

function getMfrrBidsDeliveryMethod(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined

  const { delivery_method } = data as Partial<{ delivery_method: string }>
  if (typeof delivery_method !== 'string') return undefined

  return delivery_method
}

function getMfrrBidsEventStartDate(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined

  const { start_time } = data as Partial<MfrrBidsEventData>
  if (typeof start_time !== 'string' || !Number.isFinite(Date.parse(start_time))) return undefined

  return Datetime.toLocalTime(start_time, 'onlyDate')
}

function getMfrrBidsEventEndDate(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined

  const { end_time } = data as Partial<MfrrBidsEventData>
  if (typeof end_time !== 'string' || !Number.isFinite(Date.parse(end_time))) return undefined

  return Datetime.toLocalTime(end_time, 'onlyDate')
}

function getMfrrBidsEventMarketLabel(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined

  const { market } = data as Partial<MfrrBidsEventData>
  if (market !== 'cm' && market !== 'eam') return undefined

  return market === 'eam' ? 'EAM' : 'CM'
}

// ============================================================================
// SYSTEM EVENT HANDLERS
// ============================================================================

const onOptModelHasUnusedSettingsPusherEvent = (data?: { opt_model_id: number; system: number }): void => {
  if (data) {
    queryClient.invalidateQueries({ queryKey: [SYSTEM_QUERY_KEY, data.system] })
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
// Manages all Pusher event subscriptions for the active system
// Optimization event handlers are in PusherSubscriptions.helpers.ts
// Type-specific routing logic is handled in optimizationUpdatedPusherEvent below

export default function PusherSubscriptions(): null {
  const { systemId, activeSystem, setActiveSystem } = useAuth()
  const { data: user } = useUser()
  const { t } = useTranslation()
  const hasPermissionForRegularWip = useHasPermission('view_plan_wip')
  const hasFeatureFlagForRegularWip = useSystemFeatureFlagIsActive('use_draftproductionplan')
  // `OPT_JOB_WITH_QUEUE_ITEMS_QUERY_KEY` has exactly one consumer today: the opt
  // job log in the notification center panel, which unmounts when the panel is
  // closed. That single consumer is what makes it safe to tie the cache's upkeep
  // to the panel — while it is closed, neither the `queueitem-updated` patch below
  // nor the invalidation in `optimizationUpdatedPusherEvent` runs, and the log
  // refetches on open instead (`refetchOnMount: 'always'`).
  //
  // If that query key gains a second consumer anywhere else, remove both
  // `notificationCenterIsOpen` checks: the new consumer would otherwise go stale
  // whenever the panel happens to be closed, with nothing pointing at the cause.
  const { notificationCenterIsOpen } = useSnapshot(globalStore)
  const { viewedBidWindow } = useSnapshot(mfrrStore)

  // ============================================================================
  // SYSTEM EVENTS
  // ============================================================================

  const { channel: optimizationsChannel } = useChannel(
    activeSystem ? `aurora-django-optimizations-${activeSystem.name}` : undefined
  )
  useEvent(optimizationsChannel, 'opt-model-has-unused-settings', onOptModelHasUnusedSettingsPusherEvent)

  const handleQueueItemUpdated = (data: unknown): void => {
    const parsed = QueueItemSchema.safeParse(data)
    if (!parsed.success) {
      log.error('Received invalid queueitem-updated payload:', parsed.error)
      return
    }
    patchOptJobWithQueueItemsCache(parsed.data)
  }
  // A null channel keeps this unbound while the log panel is closed — see the
  // single-consumer note on `notificationCenterIsOpen` above.
  useEvent(notificationCenterIsOpen ? optimizationsChannel : null, 'queueitem-updated', handleQueueItemUpdated)

  const { channel: systemChannel } = useChannel(activeSystem ? `aurora-django-system-${activeSystem.id}` : undefined)

  useEvent(
    systemChannel,
    'primary_model_updated',
    (data?: { primary_opt_model?: PrimaryOptModel; primary_digital_twin?: PrimaryDigitalTwin }) => {
      if (activeSystem && data) {
        const newSystem = activeSystem
        newSystem.primary_opt_model = data?.primary_opt_model
        newSystem.primary_digital_twin = data?.primary_digital_twin
        setActiveSystem(newSystem)
        authStore.activeSystem = newSystem
      }
    }
  )

  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const handleSystemUpdated = useCallback((): void => {
    queryClient.invalidateQueries({ queryKey: [SYSTEMS_QUERY_KEY] })
  }, [])
  useEvent(systemChannel, 'system_updated', handleSystemUpdated)

  useSettlementExportUpdates(systemChannel, activeSystem?.id)

  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const handleAutomaticOptimizationPusherEvent = useCallback(
    (data: { system: number; status: boolean } | undefined): void => {
      if (data) {
        queryClient.setQueryData<System | undefined>([SYSTEM_QUERY_KEY, data.system], (old) => {
          if (old) {
            data.status
              ? alertStore.info(t(`Automatic optimization activated! Optimizations will be updated once every hour.`))
              : alertStore.info(
                  t(
                    `Automatic optimization deactivated! Optimizations will now need to be executed manually in order for the production plan to update.`
                  )
                )
            return {
              ...old,
              automatic_optimization: data.status,
            }
          }
          return old
        })
      }
    },
    [t]
  )
  useEvent(optimizationsChannel, `automatic-opt-state-changed`, handleAutomaticOptimizationPusherEvent)

  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const handleAccumulatorLevelsUpdated = useCallback(
    (data: { organization: number; system: number; opt_model: number } | undefined): void => {
      if (data) {
        const { opt_model } = data
        queryClient.invalidateQueries({
          queryKey: [UNITS_QUERY_KEY, { belongs_to_opt_model: opt_model, unit_type: `Storage` }],
        })
        alertStore.info(t(`Measured values have been updated`), alertProps)
      }
    },
    [t]
  )
  useEvent(optimizationsChannel, `ack-levels-updated`, handleAccumulatorLevelsUpdated)

  // ============================================================================
  // OPTIMIZATION EVENTS
  // ============================================================================
  // Uses handler registry pattern for clean, maintainable optimization event handling
  // Each optimization type has a config that defines its behavior for each status

  // Build handler registry based on current permissions and feature flags
  const handlerRegistry = useMemo(() => {
    const registry: Partial<Record<string, OptimizationTypeConfig>> = {
      regular: hasFeatureFlagForRegularWip ? publishedRegularConfig : regularConfig,
      fuel_plan: fuelPlanConfig,
      electricity_price_scenario: electricityPriceScenarioConfig,
      measvalues_calculations: measvaluesCalculationsConfig,
      sandbox: sandboxConfig,
      real_time: realTimeConfig,
      real_time_measvalues_calculations: realTimeConfig,
      followup_without_deviations: followUpConfig,
      ...(hasPermissionForRegularWip && { regular_wip: regularWipConfig }),
    }

    return registry
  }, [hasFeatureFlagForRegularWip, hasPermissionForRegularWip])

  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const optimizationUpdatedPusherEvent = useCallback(
    async (data: OptimizationEventData | undefined): Promise<void> => {
      if (!data) return

      const { opt_project, opt_job_type: type, status, system } = data

      // ========================================
      // COMMON INVALIDATIONS (all optimization types)
      // ========================================
      queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, `latest`, type, 'Any', system] })
      queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, `latestQueueItem`, type] })

      // Gated on the open panel for the reason documented on `notificationCenterIsOpen`.
      if (notificationCenterIsOpen && OPT_JOB_WITH_QUEUE_ITEM_LOG_REFETCH_STATUSES.has(status)) {
        queryClient.invalidateQueries({ queryKey: [OPT_JOB_WITH_QUEUE_ITEMS_QUERY_KEY] })
      }

      // Invalidate review optimizations for regular and regular_wip (with permission)
      const isRegularWipWithPermission = type === 'regular_wip' && hasPermissionForRegularWip
      if (type === 'regular' || isRegularWipWithPermission) {
        queryClient.invalidateQueries({ queryKey: [OPT_JOBS_AND_QUEUE_ITEMS_STATUS_QUERY_KEY, type] })
      }

      // Handle opt_project specific invalidations
      // Note: Not really sure how this is used, as this is mostly used for Sandbox
      if (opt_project !== undefined) {
        invalidateOptProjectCommonQueries(opt_project, type, system)
        if (status === 'Finished') {
          invalidateOptProjectFinishedQueries(opt_project, type, system)
        } else {
          updateOptProjectStatus(opt_project, system, status)
        }
      }

      // ========================================
      // GET HANDLER CONFIG FOR THIS TYPE
      // ========================================
      const config = handlerRegistry[type]
      if (!config) return // Unknown type, skip

      // ========================================
      // STATUS-BASED HANDLING
      // ========================================
      const ctx: OptimizationHandlerContext = { data, user, t }

      if (config.onCommonInvalidate) {
        config.onCommonInvalidate()
      }

      if (status === 'Finished') {
        // 1. Data refresh (async, with random delay to prevent thundering herd)
        if (config.onFinishedRefresh) {
          await config.onFinishedRefresh()
        }

        // 2. Query invalidations
        if (config.onFinishedInvalidate) {
          config.onFinishedInvalidate(ctx)
        }

        // 3. User alerts
        if (config.onFinishedAlert) {
          config.onFinishedAlert(ctx)
        }
      } else if (status === 'Failed' || status === 'Unsolvable') {
        // Error alerts
        if (config.onErrorAlert) {
          config.onErrorAlert(ctx)
        }
      }
    },
    [t, user, handlerRegistry, hasPermissionForRegularWip, notificationCenterIsOpen]
  )
  useEvent(optimizationsChannel, `optimization-updated`, optimizationUpdatedPusherEvent)

  // ============================================================================
  // NOTIFICATION CENTER
  // ============================================================================

  const { channel: notificationChannel } = useChannel(systemId ? `notification-center-${systemId}` : undefined)
  useEvent(notificationChannel, `notification-create`, () => {
    queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
  })
  useEvent(notificationChannel, `notification-update`, () => {
    queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
  })
  useEvent(notificationChannel, `notification-delete`, () => {
    queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
  })

  const { channel: mfrrChannel } = useChannel(systemId ? `elplan-${systemId}` : undefined)
  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const handleLoadMfrrBidsChanges = useCallback((): void => {
    queryClient.invalidateQueries({ queryKey: [MFRR_CM_BIDS_QUERY_KEY] })
    alertStore.alertClear()
  }, [])
  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const handleMfrrBidsChanged = useCallback(
    (message: string, data: unknown): void => {
      if (!isEventInViewedMfrrBidWindow(viewedBidWindow, data)) return

      alertStore.warning(t(message), undefined, undefined, {
        action: {
          label: t('Load and update changes'),
          onClick: handleLoadMfrrBidsChanges,
        },
        note: t('Note that loading these changes will overwrite your current changes'),
      })
    },
    [handleLoadMfrrBidsChanges, viewedBidWindow, t]
  )
  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const handleMfrrBidsLocked = useCallback(
    (message: string, data: unknown): void => {
      if (!isEventInViewedMfrrBidWindow(viewedBidWindow, data)) return

      queryClient.invalidateQueries({ queryKey: [MFRR_CM_BIDS_QUERY_KEY] })
      alertStore.warning(t(message))
    },
    [viewedBidWindow, t]
  )
  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const handleMfrrBidsFetched = useCallback(
    (data: unknown): void => {
      queryClient.invalidateQueries({ queryKey: [MFRR_DELIVERED_BIDS_QUERY_KEY] })

      // We only want to trigger alert banners if bids were delivered manually by an adapter on backend (set through MfrrConfig).
      // If customer fetches bids on a recurring schedule instead, this alert would be triggered very often (and annoy the user)
      if (getMfrrBidsDeliveryMethod(data) === 'adapter') {
        const startDate = getMfrrBidsEventStartDate(data)
        const marketLabel = getMfrrBidsEventMarketLabel(data)

        let message: string
        if (marketLabel && startDate) {
          message = t('A {{market}} bid delivery has been made for {{date}}', { market: marketLabel, date: startDate })
        } else if (marketLabel) {
          message = t('A {{market}} bid delivery has been made.', { market: marketLabel })
        } else if (startDate) {
          message = t('A bid delivery has been made for {{date}}', { date: startDate })
        } else {
          message = t('A bid delivery has been made.')
        }

        alertStore.success(message, undefined, 'mfrr_bids_delivered')
      }
    },
    [t]
  )
  // conventions-allow: manual-callback - stable Pusher event listener reference
  const handleMfrrSuccessfulMarketResultsUpload = useCallback(
    (data: unknown): void => {
      queryClient.invalidateQueries({ queryKey: [MFRR_MARKET_RESULTS_QUERY_KEY] })

      const startDate = getMfrrBidsEventStartDate(data)
      const endDate = getMfrrBidsEventEndDate(data)
      const marketLabel = getMfrrBidsEventMarketLabel(data)

      let message: string
      if (marketLabel && startDate && endDate) {
        if (startDate === endDate) {
          message = t('mFRR market results have been published for {{market}} on {{date}}', {
            market: marketLabel,
            date: startDate,
          })
        } else {
          message = t('mFRR market results have been published for {{market}} from {{startDate}} to {{endDate}}', {
            market: marketLabel,
            startDate,
            endDate,
          })
        }
        alertStore.info(message, undefined, 'mfrr_successful_market_results_upload')
      }
    },
    [t]
  )
  useEvent(mfrrChannel, 'mfrr_bids_created', (data: unknown) =>
    handleMfrrBidsChanged('New mFRR bids were created for the selected date and market.', data)
  )
  useEvent(mfrrChannel, 'mfrr_bids_updated', (data: unknown) =>
    handleMfrrBidsChanged('mFRR bids were updated for the selected date and market.', data)
  )
  useEvent(mfrrChannel, 'mfrr_bids_published', (data: unknown) =>
    handleMfrrBidsLocked('mFRR bids were locked for the selected date and market.', data)
  )
  useEvent(mfrrChannel, 'mfrr_bids_unpublished', (data: unknown) =>
    handleMfrrBidsLocked('mFRR bids were unlocked for the selected date and market.', data)
  )
  useEvent(mfrrChannel, 'mfrr_bids_delivered', handleMfrrBidsFetched)
  useEvent(mfrrChannel, 'mfrr_successful_market_results_upload', handleMfrrSuccessfulMarketResultsUpload)

  // ============================================================================
  // OPERATIONAL EVENTS
  // ============================================================================

  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const handleOperationalEventChange = useCallback(() => {
    refetchOperationalEventsAfterInFlight(queryClient)
  }, [])

  const { channel: operationalEventChannel } = useChannel(
    activeSystem?.primary_digital_twin ? `operational-events-${activeSystem?.primary_digital_twin.id}` : undefined
  )
  useEvent(operationalEventChannel, `operational_event_created`, handleOperationalEventChange)
  useEvent(operationalEventChannel, `operational_event_updated`, handleOperationalEventChange)
  useEvent(operationalEventChannel, `operational_event_deleted`, handleOperationalEventChange)
  useEvent(operationalEventChannel, `operational_event_change_request_created`, handleOperationalEventChange)
  useEvent(operationalEventChannel, `operational_event_change_request_updated`, handleOperationalEventChange)
  useEvent(operationalEventChannel, `operational_event_change_request_deleted`, handleOperationalEventChange)
  useEvent(operationalEventChannel, `operational_event_change_request_approved`, handleOperationalEventChange)
  useEvent(operationalEventChannel, `operational_event_change_request_rejected`, handleOperationalEventChange)

  // ============================================================================
  // SANDBOX / OPT PROJECT EVENTS
  // ============================================================================

  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const invalidateSuccessfullyCreatedOptProjects = useCallback(
    (data: { opt_project_id: number; display_name: string } | undefined): void => {
      queryClient.invalidateQueries({ queryKey: [OPT_PROJECTS_QUERY_KEY] })
      if (data) {
        alertStore.info(
          t(`Sandbox project '{{displayName}}' has been successfully created! You can now start using the project.`, {
            displayName: data.display_name,
          }),
          alertProps
        )
      }
    },
    [t]
  )

  // conventions-allow: manual-callback - stable reference passed to a Pusher event listener
  const invalidateFailedCreationOfOptProjects = useCallback(
    (data: { opt_project_id: number; display_name: string } | undefined): void => {
      queryClient.invalidateQueries({ queryKey: [OPT_PROJECTS_QUERY_KEY] })
      if (data) {
        alertStore.error(
          t(
            `Failed to create sandbox project '{{displayName}}'. Please try again or contact us if the problem persists.`,
            {
              displayName: data.display_name,
            }
          ),
          alertProps
        )
      }
    },
    [t]
  )

  const { channel: optProjectsChannel } = useChannel(
    systemId && user ? `opt-projects-${systemId}-${user.id}` : undefined
  )
  useEvent(optProjectsChannel, `opt_project_created`, invalidateSuccessfullyCreatedOptProjects)
  useEvent(optProjectsChannel, `opt_project_creation_failed`, invalidateFailedCreationOfOptProjects)

  return null
}
